import { useState } from 'react';
import { Modal, ModalProps } from '@mui/base/Modal';
import { KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';

interface PublishArticleModalProps extends Omit<ModalProps, 'children'> {
  onConfirm: (publishedAt: string | null) => void;
  onClose: () => void;
}

const PublishArticleModal = ({
  open,
  onConfirm,
  onClose,
}: PublishArticleModalProps) => {
  const { hasPermission } = usePermissions();
  const [publishType, setPublishType] = useState<'immediate' | 'scheduled'>(
    'immediate'
  );
  const [scheduledTime, setScheduledTime] = useState('');
  const [error, setError] = useState('');

  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleConfirm = () => {
    if (publishType === 'scheduled') {
      if (!scheduledTime) {
        setError('Please select a publish date and time');
        return;
      }
      const selectedDate = new Date(scheduledTime);
      const now = new Date();
      if (selectedDate <= now) {
        setError('Publish time must be in the future');
        return;
      }
      const isoTime = `${scheduledTime}`;
      onConfirm(isoTime);
    } else {
      onConfirm(new Date().toISOString());
    }
    handleClose();
  };

  const handleClose = () => {
    setPublishType('immediate');
    setScheduledTime('');
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
        <section
          aria-labelledby="publish-article-heading"
          className="bg-gray-100 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[95dvh] overflow-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3
              id="publish-article-heading"
              className="text-lg font-semibold text-gray-900"
            >
              Publish Article
            </h3>
            <button
              onClick={handleClose}
              className="btn btn-sm btn-icon btn-light"
              aria-label="Close"
            >
              <KeenIcon icon="cross" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Choose when to publish this article
            </p>

            {/* Radio Options */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="publishType"
                  value="immediate"
                  checked={publishType === 'immediate'}
                  onChange={() => {
                    setPublishType('immediate');
                    setError('');
                  }}
                  className="radio mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900">
                    Publish Immediately
                  </div>
                  <div className="text-sm text-gray-500">
                    Article will be published right away
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="publishType"
                  value="scheduled"
                  checked={publishType === 'scheduled'}
                  onChange={() => setPublishType('scheduled')}
                  className="radio mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    Schedule for Later
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    Choose a specific date and time
                  </div>

                  {publishType === 'scheduled' && (
                    <input
                      type="datetime-local"
                      value={scheduledTime}
                      onChange={(e) => {
                        setScheduledTime(e.target.value);
                        setError('');
                      }}
                      min={getMinDateTime()}
                      className="input input-bordered w-full"
                      aria-label="Publish date and time"
                    />
                  )}
                </div>
              </label>
            </div>

            {error && (
              <div className="text-danger contrast:text-red-500 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button onClick={handleClose} className="btn btn-light">
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="btn btn-primary"
              disabled={!hasPermission('article_management_publish')}
            >
              Confirm & Publish
            </button>
          </div>
        </section>
      </div>
    </Modal>
  );
};

export { PublishArticleModal };
